/**
 * WAV header parsing — PURE helpers, no DOM/FileReader dependencies.
 *
 * Walks the RIFF chunk list to locate the "fmt " chunk (LIST/JUNK chunks may
 * precede it, so fixed offsets are not safe), then extracts the fields the
 * Looper Plus cares about. Safe to unit test under node --test.
 */

/**
 * Parse a WAV header from a DataView covering at least the first chunks of
 * the file (64 KiB is plenty in practice).
 *
 * @param {DataView} view
 * @returns {{valid: true, format: {channels:number, sampleRate:number, bitsPerSample:number}}
 *          |{valid: false, error: string}}
 */
export function parseWavHeader(view) {
    try {
        const tag = (offset) => String.fromCharCode(
            view.getUint8(offset),
            view.getUint8(offset + 1),
            view.getUint8(offset + 2),
            view.getUint8(offset + 3)
        );

        // Check RIFF header
        if (tag(0) !== 'RIFF') {
            return { valid: false, error: 'Invalid WAV file: Missing RIFF header' };
        }

        // Check WAVE format
        if (tag(8) !== 'WAVE') {
            return { valid: false, error: 'Invalid WAV file: Not WAVE format' };
        }

        // Walk the RIFF chunks to find "fmt " — don't assume it sits at a
        // fixed offset (LIST/JUNK chunks before it are common).
        let fmt = null;
        let offset = 12;
        while (offset + 8 <= view.byteLength) {
            const chunkId = tag(offset);
            const chunkSize = view.getUint32(offset + 4, true);

            if (chunkId === 'fmt ') {
                if (offset + 8 + 16 > view.byteLength) break;
                fmt = {
                    audioFormat: view.getUint16(offset + 8, true),   // 1 = PCM
                    numChannels: view.getUint16(offset + 10, true),  // 1 = Mono, 2 = Stereo
                    sampleRate: view.getUint32(offset + 12, true),   // 44100 or 48000
                    bitsPerSample: view.getUint16(offset + 22, true) // 16/24/32
                };
                break;
            }

            // Chunks are word-aligned: odd sizes are padded to even.
            offset += 8 + chunkSize + (chunkSize % 2);
        }

        if (!fmt) {
            return { valid: false, error: 'Invalid WAV file: Missing format (fmt ) chunk' };
        }

        const { audioFormat, numChannels, sampleRate, bitsPerSample } = fmt;

        if (audioFormat !== 1) {
            return { valid: false, error: 'Only uncompressed PCM WAV files are supported' };
        }

        if (numChannels !== 1 && numChannels !== 2) {
            return { valid: false, error: `Invalid channel count: ${numChannels}. Expected 1 (mono) or 2 (stereo)` };
        }

        if (sampleRate !== 44100 && sampleRate !== 48000) {
            return { valid: false, error: `Invalid sample rate: ${sampleRate}Hz. Expected 44.1kHz or 48kHz` };
        }

        if (bitsPerSample !== 16 && bitsPerSample !== 24 && bitsPerSample !== 32) {
            return { valid: false, error: `Invalid bit depth: ${bitsPerSample}-bit. Expected 16, 24, or 32-bit` };
        }

        return {
            valid: true,
            format: {
                channels: numChannels,
                sampleRate,
                bitsPerSample
            }
        };
    } catch {
        return { valid: false, error: 'Error reading WAV file header' };
    }
}
